import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Avatar, Radio, Tooltip, Icon, Popconfirm } from 'antd';
import memoizeOne from 'memoize-one';
import isEmpty from 'lodash/isEmpty';
import SentIcon from 'react-icons/lib/io/android-done';
import DeliverIcon from 'react-icons/lib/io/android-done-all';
import SeenIcon from 'react-icons/lib/md/remove-red-eye';
import moment from "moment"
import {
  TextMessage,
  TextWithMedia,
  CarouselWithButtons,
  Recharge,
  RechargeDetails,
  Offers,
  RechargeHistory,
  CheckboxWithMedia
} from 'message-types';

import { voteChatlog } from '../../../../../../data/redux/page_details/actions';

import './index.scss';

import { MESSAGE_SENDER, MESSAGE_TYPES, MESSAGE_SUBTYPES, COLORS, MESSAGE_READ_STATUS } from '../../../../../../data/config/constants';
import { formatDate, showMessage, scrollToBottom } from '../../../../../../data/config/utils';
import { BRAND_INFO, MODULE_CONFIG } from '../../../../../../data/config/urls';

import ErrorBoundary from '../../../../../../components/errorboundary';
import SelectItem from '../../../../../../components/form/selectitem';
import DotsLoader from '../../../../../../components/dotsloader';

class ChatBody extends React.PureComponent {
  state = {
    intent_type: 'correctIntent',
    selected_intent: undefined,
  };

  componentDidMount() {
    scrollToBottom(this.props.chatContainer);
  }

  componentDidUpdate(prevProps) {
    let { messages } = this.props;
    if (prevProps.messages.length !== messages.length) {
      scrollToBottom(this.props.chatContainer);
    }
  }

  isTimeStampTagVisible = memoizeOne((timestamp, prev_timestamp) => {
    const t1 = new Date(timestamp).getTime();
    const t2 = new Date(prev_timestamp).getTime();
    if ((t1 - t2) > 24 * 3600 * 1000) {
      return true;
    } else {
      return false;
    }
  });

  displayTimeStamp = memoizeOne(timestamp => {
    const current_time = new Date().getTime();
    const message_time = new Date(timestamp).getTime();
    if ((current_time - message_time) < 24 * 3600 * 1000) {
      return "Today";
    } else if ((current_time - message_time) < 2 * 24 * 3600 * 1000) {
      return "Yesterday";
    } else {
      return formatDate(timestamp, { year: "numeric", month: "short", day: "numeric" });
    }
  });

  popconfirmVisibilityChanged = visible => {
    if (!visible) {
      this.closePopconfirm();
    }
  };

  onIntentSelection = selected_intent => {
    this.setState({ selected_intent });
  };

  closePopconfirm = () => {
    this.setState({ intent_type: "correctIntent", selected_intent: undefined });
  };

  onClickUpvote = message => {
    const { intent_type } = this.state;
    if (message && message.vote && message.vote === 'upvote') {
      showMessage('warning', 'you have already upvoted this message');
    } else {
      const payload = {
        chatlogId: message.chatlogId,
        vote: 'upvote',
        correctIntent: message.NLPSnapshot.intentSnapshot.intent,
        resolution: intent_type
      };
      this.props.actions.voteChatlog(payload);
    }
  };

  onConfirmDownvote = message => {
    const { intent_type } = this.state;
    const { selected_intent } = this.state;
    if (intent_type === "correctIntent") {
      if (selected_intent) {
        const payload = {
          chatlogId: message.chatlogId,
          vote: 'downvote',
          correctIntent: selected_intent,
          resolution: intent_type
        };
        this.props.actions.voteChatlog(payload);
      }
      else {
        showMessage('warning', 'choose correct intent to downvote');
      }
    }
    else {
      const payload = {
        chatlogId: message.chatlogId,
        vote: 'downvote',
        correctIntent: '',
        resolution: intent_type
      };
      this.props.actions.voteChatlog(payload);
    }
  };

  changeIntentType = e => {
    this.setState({ intent_type: e.target.value });
  }

  renderDownvotePopupBody = () => {
    const { intents } = this.props;
    const { intent_type, selected_intent } = this.state;
    return (
      <ErrorBoundary>
        <Radio.Group size="small" onChange={this.changeIntentType} value={intent_type}>
          <Radio.Button className="ori-font-normal ori-font-xs ori-capitalize" value="correctIntent">Select Intent</Radio.Button>
          <Radio.Button className="ori-font-normal ori-font-xs ori-capitalize" value="newIntent">New Intent</Radio.Button>
          <Radio.Button className="ori-font-normal ori-font-xs ori-capitalize" value="doNotKnow">Don&apos;t Know</Radio.Button>
          {
            intent_type === "correctIntent" &&
            <SelectItem title="choose most accurate intent to downvote" placeholder="select correct intent" options={intents} value_key="name" name_key="name" value={selected_intent} onChange={this.onIntentSelection} is_option_object_type show_search />
          }
        </Radio.Group>
      </ErrorBoundary>
    );
  };

  renderTypingIndicator = () => {
    const { typing_info } = this.props;
    const senders = Object.keys(typing_info);
    if (senders.length > 0) {
      return (
        senders.map((sender, index) => {
          const is_customer_typing = sender === MESSAGE_SENDER.CUSTOMER && typing_info[sender] && typing_info[sender].isTyping;
          const is_admin_typing = sender === MESSAGE_SENDER.ADMIN && typing_info[sender] && typing_info[sender].isTyping;
          const is_chatbot_typing = sender === MESSAGE_SENDER.CHATBOT && typing_info[sender] && typing_info[sender].isTyping;
          if (is_customer_typing || is_admin_typing || is_chatbot_typing) {
            return (
              <div key={index} className={classNames("ori-flex msgContainer", { "receiverMsgContainer": is_customer_typing, "senderMsgContainer": is_admin_typing || is_chatbot_typing })}>
                <div className={classNames("ori-border-radius-10 ori-box-shadow-light ori-lr-pad-10 ori-tb-pad-5 ori-b-mrgn-4  ori-flex-row", { "ori-font-white ori-bg-primary": is_customer_typing, "ori-bg-white ori-font-primary": is_admin_typing || is_chatbot_typing })}>
                  {
                    typing_info[sender].typingMessage && typing_info[sender].typingMessage !== "" &&
                    <div className="ori-r-pad-5 ori-font-13 ori-capitalize-first">{typing_info[sender].typingMessage ? typing_info[sender].typingMessage : null}</div>
                  }
                  <div className="ori-flex-column ori-flex-jc ori-tb-pad-3"><DotsLoader dot_color={is_customer_typing ? "ori-bg-white" : "ori-bg-primary"} /></div>
                </div>
              </div>
            );
          }
        })
      )
    }
  };

  renderReadStatusIcon = readStatus => {
    switch (readStatus) {
      case MESSAGE_READ_STATUS.SENT: return (<SentIcon size={13} className="ori-l-mrgn-5" />);
      case MESSAGE_READ_STATUS.DELIVERED: return (<DeliverIcon size={13} className="ori-l-mrgn-5" />);
      case MESSAGE_READ_STATUS.SEEN: return (<SeenIcon size={13} className="ori-l-mrgn-5" />);
      default: return null;
    }
  };

  render() {
    const { messages, active_chat_item, editMessageNLPSnapshot, setChatBodyRef, show_customer_votes } = this.props;
    const title = active_chat_item && active_chat_item.udf1 && active_chat_item.udf1.trim().length > 0 ? active_chat_item.udf1 : "Unknown";
    return (
      <div ref={setChatBodyRef} className="ori-full-width ori-pad-20 oriChatBodyContainer">
        {
          messages.map((message, index) => {
            const first_msg = (index === 0) || (index > 0 && (messages[index - 1].sender !== message.sender) || (messages[index - 1].senderInfo && message.senderInfo && messages[index - 1].senderInfo.psid !== message.senderInfo.psid));

            const customer = message.sender === MESSAGE_SENDER.CUSTOMER;
            const chatbot = message.sender === MESSAGE_SENDER.CHATBOT;
            const admin = message.sender === MESSAGE_SENDER.ADMIN;
            const system = message.sender === MESSAGE_SENDER.SYSTEM;

            const show_prev_intent = message.prevNLPSnapshot && message.prevNLPSnapshot.intentSnapshot && !isEmpty(message.prevNLPSnapshot.intentSnapshot);
            const show_current_intent = message.NLPSnapshot && message.NLPSnapshot.intentSnapshot && !isEmpty(message.NLPSnapshot.intentSnapshot);

            const show_textMessage = message.type === MESSAGE_TYPES.TEXT;
            const show_whisper = message.type === MESSAGE_TYPES.WHISPER;
            const show_textWithButtons = message.type === MESSAGE_TYPES.TEXT_WITH_BUTTONS;
            const show_imageWithButtons = message.type === MESSAGE_TYPES.IMAGE_WITH_BUTTONS;
            const show_textWithVideo = message.type === MESSAGE_TYPES.VIDEO;

            const show_textWithMedia = show_textWithButtons || show_imageWithButtons || show_textWithVideo;

            const show_carousel = message.type === MESSAGE_TYPES.CAROUSEL;
            const show_checkboxWithMedia = message.type === MESSAGE_TYPES.CHECKBOX_WITH_MEDIA;

            const show_recharge = message.type === MESSAGE_TYPES.CUSTOM_MSG && message.payload.subtype === MESSAGE_SUBTYPES.DISH_RECHARGE;
            const show_rechargeDetails = message.type === MESSAGE_TYPES.CUSTOM_MSG && message.payload.subtype === MESSAGE_SUBTYPES.DISH_RECHARGE_DETAILS;

            const show_offers = message.type === MESSAGE_TYPES.CUSTOM_MSG && message.payload.subtype === MESSAGE_SUBTYPES.DISH_OFFERS;
            const show_rechargeHistory = message.type === MESSAGE_TYPES.CUSTOM_MSG && message.payload.subtype === MESSAGE_SUBTYPES.DISH_RECHARGE_HISTORY;

            let sender_title = "u";
            let sender_img_url = "";
            if (message.senderInfo) {
              sender_title = message.senderInfo.pseudoName ? message.senderInfo.pseudoName : "u";
              sender_img_url = message.senderInfo.imgUrl && message.senderInfo.imgUrl.trim().length > 0 ? message.senderInfo.imgUrl : "";
            }

            const show_timestamp_tag = index === 0 ? true : (message.timestamp ? this.isTimeStampTagVisible(message.timestamp, messages[index - 1].timestamp) : false);

            if (system && message.type === MESSAGE_TYPES.SYSTEM_TEXT) {
              return (
                <ErrorBoundary key={index}>
                  <div className="ori-tb-pad-15 ori-lr-pad-20 ori-text-center ">
                    <p className="ori-font-medium">{message.payload.text}</p>
                    {
                      message.timestamp &&
                      <p className="ori-font-xxs ori-lr-pad-10">{moment(message.timestamp).format("hh:mm A")}</p>
                    }
                  </div>
                </ErrorBoundary>
              );
            }

            return (
              <ErrorBoundary key={index}>
                <div className="ori-full-width">
                  {
                    show_timestamp_tag &&
                    <div className="ori-flex-row ori-flex-jc">
                      <div className="ori-font-xs ori-border-radius-20 ori-lr-pad-10 ori-b-mrgn-10 ori-t-mrgn-15 ori-bg-default ori-box-shadow">{this.displayTimeStamp(message.timestamp)}</div>
                    </div>
                  }
                  <div className={classNames("ori-relative ori-flex msgContainer ", { "receiverMsgContainer": customer, "senderMsgContainer": chatbot || admin })}>
                    {
                      first_msg && message.senderInfo && message.senderInfo.pseudoName &&
                      <p className="ori-absolute ori-font-11 ori-capitalize ori-align-top-5" >{message.senderInfo.pseudoName}</p>
                    }
                    {
                      first_msg && customer &&
                      <div className={classNames("ori-absolute ori-animated ori-fade-in msgAvatar")}>
                        <Avatar className="ori-capitalize ori-align-avatar-letter ori-bg-primary">{title.charAt(0)}</Avatar>
                      </div>
                    }
                    {
                      first_msg && chatbot &&
                      <div className={classNames("ori-absolute ori-animated ori-fade-in msgAvatar")}>
                        <Avatar src={sender_img_url !== "" ? sender_img_url : BRAND_INFO.bot_icon_url} />
                      </div>
                    }
                    {
                      first_msg && admin &&
                      <div className={classNames("ori-absolute ori-animated ori-fade-in msgAvatar")}>
                        <Avatar src={sender_img_url} className="ori-font-default ori-capitalize ori-align-avatar-letter ori-bg-white">{sender_title.charAt(0)}</Avatar>
                      </div>
                    }
                    {
                      (show_textMessage || show_textWithMedia || show_recharge || show_rechargeDetails || show_offers || show_rechargeHistory || show_carousel || show_whisper || show_checkboxWithMedia) &&
                      <div className="ori-b-mrgn-4">
                        <div className={classNames("ori-flex", { "ori-t-pad-20": first_msg, "bubbleIndicatorRightPrimary": first_msg && customer, "bubbleIndicatorLeftWhite": first_msg && (chatbot || admin) && !show_whisper, "bubbleIndicatorLeftDefault": first_msg && admin && show_whisper })}>
                          <div className={classNames("ori-animated ori-fade-in ori-border-radius-10 ori-box-shadow-light ori-lr-pad-10 ori-tb-pad-5 msgBox", { "bubbleIndicator": first_msg, "ori-bg-primary ori-font-white ": customer, "ori-bg-white": (chatbot || admin) && !show_whisper, "ori-bg-default": show_whisper }
                          )}>
                            {
                              (show_textMessage || show_whisper) &&
                              <TextMessage message={message} editMessageNLPSnapshot={editMessageNLPSnapshot} show_nlp_snapshot />
                            }
                            {
                              show_textWithMedia &&
                              <TextWithMedia message={message} btn_disabled />
                            }
                            {
                              show_recharge &&
                              <Recharge message={message} btn_disabled />
                            }
                            {
                              show_rechargeDetails &&
                              <RechargeDetails message={message} btn_disabled />
                            }
                            {
                              show_offers &&
                              <Offers message={message} disable_offer btn_disabled />
                            }
                            {
                              show_rechargeHistory &&
                              <RechargeHistory message={message} btn_disabled />
                            }
                            {
                              show_carousel &&
                              <CarouselWithButtons message={message} btn_disabled />
                            }
                            {
                              show_checkboxWithMedia &&
                              <CheckboxWithMedia message={message} btn_disabled checkbox_disabled />
                            }
                            {
                              (message.timestamp || message.readStatus) &&
                              <div className={classNames("ori-font-xxs ori-flex-row ori-line-height-1", { "ori-font-white": customer, "ori-font-default": chatbot || admin }, { "ori-flex-jsb": show_customer_votes, "ori-flex-jfe": !show_customer_votes })}>
                                {
                                  (chatbot || admin) && show_customer_votes &&
                                  <div className="ori-flex-row ori-flex-jfs">
                                    <Icon className="ori-cursor-ptr ori-font-xs ori-t-pad-3" type="like" theme="twoTone" twoToneColor={message.customerVote === "upvote" ? COLORS.green : COLORS.fontcolor_light} />
                                    <Icon className="ori-cursor-ptr ori-font-xs ori-rotate-180 ori-lr-mrgn-5" type="like" theme="twoTone" twoToneColor={message.customerVote === "downvote" ? COLORS.danger : COLORS.fontcolor_light} />
                                  </div>
                                }
                                <div className="ori-flex-row ori-flex-jfe ori-t-mrgn-3">
                                  {
                                    message.timestamp &&
                                    <span className="ori-t-pad-3">{moment(message.timestamp).format("HH:mm:ss")}</span>
                                  }
                                  {
                                    admin && message.readStatus &&
                                    this.renderReadStatusIcon(message.readStatus)
                                  }
                                </div>
                              </div>
                            }
                          </div>
                          {
                            customer && show_current_intent &&
                            <div className="ori-flex-column ori-l-pad-5">
                              <Icon className="ori-cursor-ptr ori-font-md ori-b-pad-5" type="like" theme="twoTone" twoToneColor={message.vote === "upvote" ? COLORS.green : COLORS.fontcolor_light} onClick={this.onClickUpvote.bind(this, message)} />
                              <Popconfirm overlayClassName="ori-popover-message-no-padding" icon={null} title={this.renderDownvotePopupBody()} onCancel={this.closeIntentPopconfirm} onConfirm={this.onConfirmDownvote.bind(this, message)} onVisibleChange={this.popconfirmVisibilityChanged}>
                                <Icon className="ori-cursor-ptr ori-font-md ori-rotate-180 " type="like" theme="twoTone" twoToneColor={message.vote === "downvote" ? COLORS.danger : COLORS.fontcolor_light} />
                              </Popconfirm>
                            </div>
                          }
                        </div>
                        {
                          customer &&
                          <div className="ori-flex-row ori-t-pad-3">

                            {
                              show_prev_intent &&
                              <Tooltip overlayClassName="intentTooltip" placement="bottomLeft" title={message.prevNLPSnapshot.intentSnapshot.score}>
                                <div className="ori-animated ori-fade-in ori-bg-default ori-lr-pad-6 ori-border-radius-10 ori-font-11 ori-capitalize ori-cursor-ptr ori-line-through" onClick={editMessageNLPSnapshot.bind(this, message)}>{message.prevNLPSnapshot.intentSnapshot.intent}</div>
                              </Tooltip>
                            }
                            {
                              show_current_intent &&
                              <Tooltip overlayClassName="intentTooltip" placement="bottomLeft" title={message.NLPSnapshot.intentSnapshot.score}>
                                <div className={`ori-animated ori-fade-in ori-bg-default ori-lr-pad-6 ori-l-mrgn-5 ori-border-radius-10 ori-font-11 ori-capitalize ori-cursor-ptr ${message.NLPSnapshot.intentSnapshot.score < MODULE_CONFIG.GLOBAL.INTENT_THRESHOLD_SCORE ? "ori-font-danger" : "ori-font-primary"}`} onClick={editMessageNLPSnapshot.bind(this, message)}>{message.NLPSnapshot.intentSnapshot.intent}</div>
                              </Tooltip>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>
              </ErrorBoundary>
            );
          })
        }
        {this.renderTypingIndicator()}
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    admin_details: state.admin_details,
    intents: state.nlp_details.intents,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators({ voteChatlog }, dispatch),
  };
};

ChatBody.propTypes = {
  messages: PropTypes.array.isRequired,
  actions: PropTypes.object,
  admin_details: PropTypes.object,
  intents: PropTypes.array,
  active_chat_item: PropTypes.object,
  typing_info: PropTypes.object,
  editMessageNLPSnapshot: PropTypes.func,
  chatContainer: PropTypes.object,
  setChatBodyRef: PropTypes.func,
  show_customer_votes: PropTypes.bool,
};

ChatBody.defaultProps = {
  messages: [],
  typing_info: {},
  editMessageNLPSnapshot: () => { },
  show_customer_votes: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatBody);



// WEBPACK FOOTER //
// ./src/app/pagecontainer/components/chatinterface/components/chatbody/index.js